import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { DollarSign, Calculator, Save, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { MarkupPercentField, type MarkupInput } from './FixedBidMarkup';
import { firmDefaultMarkupPercent, fixedBidQuote, parseMarkupPercent } from '../utils/fixedBidMarkup';

interface FirmScopingSettingsProps {
  accessToken: string;
  firmId?: string;
}

export function FirmScopingSettings({ accessToken, firmId }: FirmScopingSettingsProps) {
  const [hourlyRate, setHourlyRate] = useState<number>(150);
  // The markup new fixed bids start from (Sizing, the SOW generator, new proposals).
  const [markupPercent, setMarkupPercent] = useState<MarkupInput>(0);
  const markupCheck = parseMarkupPercent(markupPercent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (firmId) {
      loadFirmData();
    }
  }, [firmId]);

  const loadFirmData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${firmId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.firm?.defaultHourlyRate) {
          setHourlyRate(data.firm.defaultHourlyRate);
        }
        setMarkupPercent(firmDefaultMarkupPercent(data.firm));
      }
    } catch (error) {
      console.error('Error loading firm data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!firmId) return;

    setSaving(true);
    setSuccess(false);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/scoping`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            defaultHourlyRate: hourlyRate,
            defaultFixedBidMarkupPercent: markupCheck.ok ? markupCheck.percent : markupPercent,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update scoping settings');
      }

      setSuccess(true);
      toast.success('Scoping settings updated successfully');
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Update scoping settings error:', err);
      toast.error(err.message || 'Failed to update scoping settings');
    } finally {
      setSaving(false);
    }
  };

  if (!firmId) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-600" />
          <div>
            <CardTitle>Scoping & Pricing Settings</CardTitle>
            <CardDescription>
              Configure default rates for automatic effort and pricing calculations
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {success && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Scoping settings updated.
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading settings...
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <Label htmlFor="hourly-rate">Default Hourly Rate</Label>
                <div className="flex items-center gap-2 mt-2">
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  <Input
                    id="hourly-rate"
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(parseInt(e.target.value) || 0)}
                    placeholder="150"
                    min="0"
                    step="5"
                    className="max-w-xs"
                  />
                  <span className="text-sm text-gray-500">per hour</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This rate will be used for automatic pricing calculations when scoping new client engagements
                </p>
              </div>

              <div className="max-w-xs">
                <MarkupPercentField
                  id="default-markup"
                  label="Default fixed-bid markup (%)"
                  value={markupPercent}
                  onChange={setMarkupPercent}
                  hint={markupCheck.ok
                    ? `New fixed bids start with this contingency buffer; each bid can change it. A $100,000 bid becomes $${fixedBidQuote(100000, markupPercent).total.toLocaleString('en-US')}.`
                    : markupCheck.error}
                />
              </div>

              {/* Preview */}
              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Scoping Preview
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Example: 100 hours</span>
                    <span className="font-medium">${(hourlyRate * 100).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Example: 200 hours</span>
                    <span className="font-medium">${(hourlyRate * 200).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Example: 500 hours</span>
                    <span className="font-medium">${(hourlyRate * 500).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={saving || hourlyRate <= 0 || !markupCheck.ok}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>

            <Alert>
              <DollarSign className="h-4 w-4" />
              <AlertDescription>
                <strong>How This Works:</strong> When creating templates or scoping new clients,
                each scope item can have estimated hours. The system will automatically calculate
                total effort and pricing using this hourly rate.
              </AlertDescription>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
}
